import { useState } from 'react'
import { X, Mail, ArrowLeft, AlertCircle, Eye, EyeOff, HelpCircle, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { addConnectedAccount } from '../../services/userService'

interface AddAccountModalProps {
  onClose: () => void
  onAccountAdded: () => void
}

type Provider = 'gmail' | 'outlook' | 'hotmail' | 'imap'
type Step = 'select' | 'gmail-config' | 'outlook-config' | 'imap-config'

const PROVIDER_COLORS = {
  gmail: '#EA4335',
  outlook: '#0078D4',
  hotmail: '#0078D4',
  imap: '#6B7280'
}

export default function AddAccountModal({ onClose, onAccountAdded }: AddAccountModalProps) {
  const { user } = useAuth()
  const [step, setStep] = useState<Step>('select')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showSetupGuide, setShowSetupGuide] = useState(false)

  // Gmail form state
  const [gmailForm, setGmailForm] = useState({
    email: '',
    appPassword: ''
  })

  // IMAP form state
  const [imapForm, setImapForm] = useState({
    email: '',
    password: '',
    imapServer: '',
    imapPort: '993',
    smtpServer: '',
    smtpPort: '587',
    useSSL: true
  })

  const handleProviderSelect = (provider: Provider) => {
    setError(null)
    setShowSetupGuide(false)

    if (provider === 'gmail') {
      setStep('gmail-config')
    } else if (provider === 'outlook' || provider === 'hotmail') {
      setStep('outlook-config')
    } else if (provider === 'imap') {
      setStep('imap-config')
    }
  }

  const handleGmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    // Validate
    if (!gmailForm.email.includes('@gmail.com') && !gmailForm.email.includes('@googlemail.com')) {
      setError('Please enter a valid Gmail address')
      return
    }

    if (gmailForm.appPassword.replace(/\s/g, '').length !== 16) {
      setError('App Password should be 16 characters (spaces are optional)')
      return
    }

    setLoading(true)
    setError(null)

    try {
      await addConnectedAccount(user.uid, {
        email: gmailForm.email,
        provider: 'gmail',
        displayName: gmailForm.email.split('@')[0],
        color: PROVIDER_COLORS.gmail,
        appPassword: gmailForm.appPassword.replace(/\s/g, ''),
        imapServer: 'imap.gmail.com',
        imapPort: 993,
        smtpServer: 'smtp.gmail.com',
        smtpPort: 587
      })
      onAccountAdded()
    } catch (err) {
      console.error('Gmail error:', err)
      setError(err instanceof Error ? err.message : 'Failed to add Gmail account')
    } finally {
      setLoading(false)
    }
  }

  const handleOutlookSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    setLoading(true)
    setError(null)

    try {
      // For Outlook, we'll also use app password approach
      await addConnectedAccount(user.uid, {
        email: imapForm.email,
        provider: imapForm.email.includes('@hotmail') || imapForm.email.includes('@outlook') ? 'hotmail' : 'outlook',
        displayName: imapForm.email.split('@')[0],
        color: PROVIDER_COLORS.outlook,
        appPassword: imapForm.password,
        imapServer: 'outlook.office365.com',
        imapPort: 993,
        smtpServer: 'smtp.office365.com',
        smtpPort: 587
      })
      onAccountAdded()
    } catch (err) {
      console.error('Outlook error:', err)
      setError(err instanceof Error ? err.message : 'Failed to add Outlook account')
    } finally {
      setLoading(false)
    }
  }

  const handleImapSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    setLoading(true)
    setError(null)

    try {
      await addConnectedAccount(user.uid, {
        email: imapForm.email,
        provider: 'imap',
        displayName: imapForm.email.split('@')[0],
        color: PROVIDER_COLORS.imap,
        appPassword: imapForm.password,
        imapServer: imapForm.imapServer,
        imapPort: parseInt(imapForm.imapPort),
        smtpServer: imapForm.smtpServer,
        smtpPort: parseInt(imapForm.smtpPort)
      })
      onAccountAdded()
    } catch (err) {
      console.error('IMAP error:', err)
      setError(err instanceof Error ? err.message : 'Failed to add IMAP account')
    } finally {
      setLoading(false)
    }
  }

  const ProviderButton = ({
    provider,
    name,
    icon,
    description
  }: {
    provider: Provider
    name: string
    icon: React.ReactNode
    description: string
  }) => (
    <button
      onClick={() => handleProviderSelect(provider)}
      className="w-full flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-primary-50 transition-all text-left"
    >
      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
        {icon}
      </div>
      <div className="flex-1">
        <p className="font-medium text-gray-900">{name}</p>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </button>
  )

  const getStepTitle = () => {
    switch (step) {
      case 'select': return 'Add Email Account'
      case 'gmail-config': return 'Connect Gmail'
      case 'outlook-config': return 'Connect Outlook'
      case 'imap-config': return 'IMAP Configuration'
      default: return 'Add Account'
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            {step !== 'select' && (
              <button
                onClick={() => {
                  setStep('select')
                  setError(null)
                  setShowSetupGuide(false)
                }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <h3 className="font-semibold text-gray-900">{getStepTitle()}</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle size={18} className="flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Provider Selection */}
          {step === 'select' && (
            <div className="space-y-3">
              <ProviderButton
                provider="gmail"
                name="Gmail"
                description="Connect with App Password"
                icon={
                  <svg className="w-8 h-8" viewBox="0 0 24 24">
                    <path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                  </svg>
                }
              />

              <ProviderButton
                provider="outlook"
                name="Office 365 / Outlook"
                description="Connect with App Password"
                icon={
                  <svg className="w-8 h-8" viewBox="0 0 24 24">
                    <path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.58.23h-8.547v-6.959l1.6 1.229c.102.078.224.117.365.117.142 0 .264-.04.365-.117l6.797-5.222c.078-.063.14-.14.186-.23.047-.09.052-.18.052-.27V7.1c0-.193-.1-.29-.297-.29h-.107c-.063.018-.128.05-.195.098l-6.801 5.218c-.102.078-.223.117-.365.117-.142 0-.263-.04-.365-.117l-6.8-5.218c-.068-.049-.133-.08-.196-.098h-.108c-.197 0-.296.097-.296.29v.12c0 .09.018.18.052.27.047.09.108.167.187.23l6.796 5.222c.102.078.224.117.365.117.142 0 .264-.04.365-.117l1.6-1.23v6.96H.82c-.228 0-.42-.077-.58-.23-.157-.152-.238-.347-.238-.576V7.387c0-.326.15-.593.45-.8L11.56 0c.18-.102.333-.102.455 0l11.534 6.587c.3.207.45.474.45.8z"/>
                  </svg>
                }
              />

              <ProviderButton
                provider="imap"
                name="Other (IMAP)"
                description="Connect any email with IMAP settings"
                icon={<Mail className="w-8 h-8 text-gray-600" />}
              />
            </div>
          )}

          {/* Gmail Configuration */}
          {step === 'gmail-config' && (
            <form onSubmit={handleGmailSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gmail Address
                </label>
                <input
                  type="email"
                  value={gmailForm.email}
                  onChange={(e) => setGmailForm({ ...gmailForm, email: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="you@gmail.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  App Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={gmailForm.appPassword}
                    onChange={(e) => setGmailForm({ ...gmailForm, appPassword: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none pr-10 font-mono"
                    placeholder="xxxx xxxx xxxx xxxx"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  16-character password from Google (spaces optional)
                </p>
              </div>

              {/* Setup Instructions */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowSetupGuide(!showSetupGuide)}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <HelpCircle size={16} />
                    How to get an App Password
                  </div>
                  {showSetupGuide ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
                </button>

                {showSetupGuide && (
                  <div className="p-4 space-y-4 text-sm border-t border-gray-200">
                    <div>
                      <div className="flex items-center gap-2 font-medium text-gray-800 mb-2">
                        <span className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs">1</span>
                        Enable 2-Step Verification
                      </div>
                      <p className="text-gray-600 ml-7">
                        Go to your{' '}
                        <a
                          href="https://myaccount.google.com/security"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline inline-flex items-center gap-1"
                        >
                          Google Account Security
                          <ExternalLink size={12} />
                        </a>
                        {' '}and enable 2-Step Verification if not already enabled.
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 font-medium text-gray-800 mb-2">
                        <span className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs">2</span>
                        Generate App Password
                      </div>
                      <p className="text-gray-600 ml-7">
                        Visit{' '}
                        <a
                          href="https://myaccount.google.com/apppasswords"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline inline-flex items-center gap-1"
                        >
                          Google App Passwords
                          <ExternalLink size={12} />
                        </a>
                        , select "Mail" and your device, then click Generate.
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 font-medium text-gray-800 mb-2">
                        <span className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs">3</span>
                        Copy the Password
                      </div>
                      <p className="text-gray-600 ml-7">
                        Copy the 16-character password shown and paste it above.
                      </p>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 ml-7">
                      <p className="text-amber-800 text-xs">
                        <strong>Security:</strong> App Passwords can be revoked anytime from your Google Account. Your main password is never stored.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !gmailForm.email || !gmailForm.appPassword}
                className="w-full py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Adding Account...' : 'Add Gmail Account'}
              </button>
            </form>
          )}

          {/* Outlook Configuration */}
          {step === 'outlook-config' && (
            <form onSubmit={handleOutlookSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={imapForm.email}
                  onChange={(e) => setImapForm({ ...imapForm, email: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="you@outlook.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  App Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={imapForm.password}
                    onChange={(e) => setImapForm({ ...imapForm, password: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none pr-10 font-mono"
                    placeholder="Your app password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Setup Instructions */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowSetupGuide(!showSetupGuide)}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <HelpCircle size={16} />
                    How to get an App Password
                  </div>
                  {showSetupGuide ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
                </button>

                {showSetupGuide && (
                  <div className="p-4 space-y-3 text-sm border-t border-gray-200">
                    <p className="text-gray-600">
                      Visit{' '}
                      <a
                        href="https://account.microsoft.com/security"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:underline inline-flex items-center gap-1"
                      >
                        Microsoft Security Settings
                        <ExternalLink size={12} />
                      </a>
                      , enable two-factor authentication, then create an app password.
                    </p>
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !imapForm.email || !imapForm.password}
                className="w-full py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Adding Account...' : 'Add Outlook Account'}
              </button>
            </form>
          )}

          {/* IMAP Configuration Form */}
          {step === 'imap-config' && (
            <form onSubmit={handleImapSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={imapForm.email}
                  onChange={(e) => setImapForm({ ...imapForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password / App Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={imapForm.password}
                    onChange={(e) => setImapForm({ ...imapForm, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none pr-10"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IMAP Server
                  </label>
                  <input
                    type="text"
                    value={imapForm.imapServer}
                    onChange={(e) => setImapForm({ ...imapForm, imapServer: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="imap.example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Port
                  </label>
                  <input
                    type="number"
                    value={imapForm.imapPort}
                    onChange={(e) => setImapForm({ ...imapForm, imapPort: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="993"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SMTP Server
                  </label>
                  <input
                    type="text"
                    value={imapForm.smtpServer}
                    onChange={(e) => setImapForm({ ...imapForm, smtpServer: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="smtp.example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Port
                  </label>
                  <input
                    type="number"
                    value={imapForm.smtpPort}
                    onChange={(e) => setImapForm({ ...imapForm, smtpPort: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="587"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Adding Account...' : 'Add Account'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
